// ===========================================
// Regenerate Backup Codes Dialog Component
// ===========================================
// Verify identity and display new backup codes

import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useRegenerateBackupCodes } from '../../../hooks/useTwoFactor';
import BackupCodesDisplay from './BackupCodesDisplay';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function RegenerateBackupCodesDialog({ open, onClose }: Props) {
  const [code, setCode] = useState('');
  const [newCodes, setNewCodes] = useState<string[] | null>(null);

  const regenerate = useRegenerateBackupCodes();

  const handleRegenerate = async () => {
    try {
      const result = await regenerate.mutateAsync({ code });
      if (result?.data?.codes) {
        setNewCodes(result.data.codes);
      }
    } catch {
      // Error handled by hook's onError
    }
  };

  const handleClose = () => {
    setCode('');
    setNewCodes(null);
    regenerate.reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={newCodes ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Regenerate Backup Codes</DialogTitle>
      <DialogContent>
        {newCodes ? (
          <Box>
            <Typography variant="body1" fontWeight="bold" sx={{ mb: 2 }}>
              New backup codes generated!
            </Typography>
            <BackupCodesDisplay codes={newCodes} />
          </Box>
        ) : (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Your existing backup codes will be invalidated and replaced with new ones.
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter a 6-digit code from your authenticator app to confirm.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label="Authenticator Code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              inputProps={{
                maxLength: 6,
                inputMode: 'numeric',
                style: { fontFamily: 'monospace', fontSize: '1.2rem', letterSpacing: 8, textAlign: 'center' },
              }}
              error={regenerate.isError}
              helperText={regenerate.isError ? 'Invalid code. Please try again.' : ''}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {newCodes ? (
          <Button variant="contained" onClick={handleClose}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleRegenerate}
              disabled={code.length !== 6 || regenerate.isPending}
            >
              {regenerate.isPending ? 'Regenerating...' : 'Regenerate'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
